import { containsUnprintableOrControlCharacters } from "./containsUnprintableOrControlCharacters";
import { base64Uint8Array } from "./utils/base64Uint8Array";

const validBase64Chars = new Set<string>("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/".split(""));
const validBase64UrlChars = new Set<string>("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_".split(""));
// const validBase64CharsAndUrlChars = new Set<string>("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/-_".split(""));
// spell-checker:disable-next-line
const validHexChars = new Set<string>("ABCDEFabcdefx0123456789+/".split(""));

export const isBase64 = (content: string): boolean => {
	if (content.length % 4 != 0) return false;
	let hasOnlyHexChars = true;
	let i = 0;
	while (i < content.length && validBase64Chars.has(content.charAt(i))) {
		hasOnlyHexChars = hasOnlyHexChars && validHexChars.has(content.charAt(i));
		i++;
	};
	if (hasOnlyHexChars) return false;
	const charsRemaining = content.length - i;
	if (charsRemaining > 3) return false;
	return charsRemaining === 0 || content.slice(i).split('').every(c => c === "=");
}

export const isBase64Url = (content: string): boolean => {
	let hasOnlyHexChars = true;
	let i = 0;
	while (i < content.length && validBase64UrlChars.has(content.charAt(i))) {
		hasOnlyHexChars = hasOnlyHexChars && validHexChars.has(content.charAt(i));
		i++;
	};
	if (hasOnlyHexChars) return false;
	const charsRemaining = content.length - i;
	if (charsRemaining > 3) return false;
	return charsRemaining === 0 || content.slice(i).split('').every(c => c === ".");
}

export type TextDataDecoding = "base64Decode" | "base64Encode" | "utf8";
export type TextDataDecodings = BinaryDataDecoding[]; 
export type BinaryDataDecoding = TextDataDecoding | "gzip";
export type BinaryDataDecodings = BinaryDataDecoding[]; 

export const decodeBase64WherePossible = (content: string): { content: string; decodings: TextDataDecodings } => {
	const decodings: TextDataDecodings = [];
	let decodingsCompleted: number | undefined;
	while (decodingsCompleted !== decodings.length) {
		decodingsCompleted = decodings.length;
		if (typeof content === "string" && content.length >= 12 && isBase64(content)) {
			// Decode base64 content
			try {
				const binary = base64Uint8Array(content).buffer;
				const utf8 = new TextDecoder("utf-8", { fatal: true }).decode(binary);
				content = utf8;
				decodings.push("base64Decode", "utf8");
			} catch { /* ignore errors */ }
		}
	}
	return { content, decodings };
}

export const decodeBase64AndGZipWherePossible = async (content: string | ArrayBuffer | undefined): Promise<{ content: string; decodings: BinaryDataDecodings } | undefined> => {
	if (content == null) return;
	const decodings: BinaryDataDecodings = [];
	let decodingsCompleted: number | undefined;
	while (decodingsCompleted !== decodings.length) {
		decodingsCompleted = decodings.length;
		const decodingsPending: BinaryDataDecodings = [];
		let contentArrayBuffer: ArrayBuffer | undefined = content != null && content instanceof ArrayBuffer ? content : undefined;

		if (typeof content === "string" && content.length >= 12 && isBase64(content)) {
			// Decode base64 content
			try {
				contentArrayBuffer = base64Uint8Array(content).buffer;
				decodingsPending.push("base64Decode");	
			} catch { /* ignore errors */}
		}
		if (contentArrayBuffer != null) {
			// Attempt to decompress with GZIP in case the content is GZIP encoded.
			try {
				const gzipBlob: Blob = new Blob([contentArrayBuffer], { type: 'application/gzip' });
				const ds = new DecompressionStream('gzip');
				const decompressedStream = gzipBlob.stream().pipeThrough(ds);
				const decompressedArrayBuffer = await new Response(decompressedStream).arrayBuffer();
				contentArrayBuffer = decompressedArrayBuffer;
				decodingsPending.push("gzip");
			} catch { /* ignore errors */}
		}
		if (contentArrayBuffer != null) {
			// Attempt to decode as UTF-8 text
			try {
				const utf8 = new TextDecoder("utf-8", { fatal: true }).decode(contentArrayBuffer);
				if (!containsUnprintableOrControlCharacters(utf8)) {
					content = utf8;
					decodings.push(...decodingsPending, "utf8");
				} else {
					try {
						content = btoa(String.fromCharCode(...new Uint8Array(contentArrayBuffer)));
						decodings.push(...decodingsPending, "base64Encode");
						return { content, decodings };
					} catch {
						/* ignore errors */
					}
				}
			} catch { /* ignore errors */}
		}
	}
	if (typeof content === "string") {
		return { content, decodings };
	}
};
